/**
 * Formatação em português do Brasil, na língua da gráfica.
 *
 * Tempo aparece como `h:mm` porque é assim que a OS imprime ("9:40" para os
 * envelopes). Trocar para "9h40min" na tela do operador seria pedir a ele que
 * traduza o que já lê no papel.
 */

import { DESLOCAMENTO_MIN, partes } from './calendario.js';

const DIAS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const dois = (n: number): string => String(n).padStart(2, '0');

/** 3600 → "1:00". Aceita negativo (desvio): −900 → "−0:15". */
export function hhmm(segundos: number | null | undefined): string {
  if (segundos === null || segundos === undefined) return '—';
  const sinal = segundos < 0 ? '−' : '';
  const s = Math.abs(Math.round(segundos));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${sinal}${h}:${dois(m)}`;
}

/** "1:02" → 3720. Devolve null para texto que não é h:mm. */
export function segundosDeHhmm(texto: string): number | null {
  const m = /^(\d+):([0-5]\d)$/.exec(texto.trim());
  if (!m) return null;
  return Number(m[1]) * 3600 + Number(m[2]) * 60;
}

/** "04/09/2026". */
export function data(iso: string | null | undefined): string {
  if (!iso) return '—';
  const p = partes(iso, DESLOCAMENTO_MIN);
  return `${dois(p.dia)}/${dois(p.mes)}/${p.ano}`;
}

/** "14:07". */
export function hora(iso: string | null | undefined): string {
  if (!iso) return '—';
  const p = partes(iso, DESLOCAMENTO_MIN);
  return `${dois(p.hora)}:${dois(p.minuto)}`;
}

/** "04/09 14:07". */
export function dataHoraCurta(iso: string | null | undefined): string {
  if (!iso) return '—';
  const p = partes(iso, DESLOCAMENTO_MIN);
  return `${dois(p.dia)}/${dois(p.mes)} ${dois(p.hora)}:${dois(p.minuto)}`;
}

/** "Sex, 04/09/2026 às 14:07" — como a OS escreve o prazo. */
export function dataHoraLonga(iso: string | null | undefined): string {
  if (!iso) return '—';
  const p = partes(iso, DESLOCAMENTO_MIN);
  return `${DIAS[p.diaSemana]}, ${dois(p.dia)}/${dois(p.mes)}/${p.ano} às ${dois(p.hora)}:${dois(p.minuto)}`;
}

/**
 * Distância no tempo, do jeito que se fala: "em 3 dias", "há 2 h", "agora".
 * Positivo é futuro.
 */
export function distanciaNoTempo(iso: string, agora: string = new Date().toISOString()): string {
  const d = Date.parse(iso) - Date.parse(agora);
  const futuro = d >= 0;
  const s = Math.abs(d) / 1000;
  const dizer = (n: number, un: string) => `${futuro ? 'em ' : 'há '}${n} ${un}`;
  if (s < 90) return futuro ? 'agora' : 'agora mesmo';
  if (s < 3600) return dizer(Math.round(s / 60), 'min');
  if (s < 86_400) {
    const h = Math.round(s / 3600);
    return dizer(h, h === 1 ? 'hora' : 'horas');
  }
  const dias = Math.round(s / 86_400);
  return dizer(dias, dias === 1 ? 'dia' : 'dias');
}

/** 2000 → "2.000"; 0.29 → "0,29". */
export function numero(n: number | null | undefined, casas = 0): string {
  if (n === null || n === undefined) return '—';
  return n.toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas });
}

/** Quantidade com unidade, respeitando as casas que a OS usa. */
export function quantidade(n: number | null | undefined, unidade?: string | null): string {
  if (n === null || n === undefined) return '—';
  const casas = Number.isInteger(n) ? 0 : 2;
  const txt = numero(n, casas);
  return unidade ? `${txt} ${unidade}` : txt;
}

/** "+38%" / "−12%". Vazio quando não há previsto. */
export function desvio(pct: number | null): string {
  if (pct === null || !Number.isFinite(pct)) return '—';
  const sinal = pct >= 0 ? '+' : '−';
  return `${sinal}${Math.abs(pct).toFixed(0)}%`;
}

/** Plural sem gambiarra: `plural(1,'OS','OS')`, `plural(3,'etapa','etapas')`. */
export function plural(n: number, um: string, muitos: string): string {
  return n === 1 ? um : muitos;
}

/** "3 OS atrasadas" / "1 OS atrasada". */
export function contagem(n: number, um: string, muitos: string): string {
  return `${numero(n)} ${plural(n, um, muitos)}`;
}
